import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { cn } from "../../utils/cn";
import { dismissAnnouncement } from "./announcements.storage";

// ─────────────────────────────────────────────────────────────────────────────
// AnnouncementModal — UI genérica de anuncio post-login con carrusel.
//
// No sabe NADA del contenido: recibe un descriptor (ver registry.js) y pinta
// el carrusel de pasos, los puntos de progreso, el checkbox "no volver a
// mostrar" (persistido por id vía announcements.storage) y el CTA final.
//
// Descriptor esperado:
//   {
//     id:    string único y estable — clave del descarte en localStorage
//     title: ReactNode — encabezado del modal
//     steps: [{ icon: ReactNode, title: ReactNode, body: ReactNode }]
//     cta?:  { label: ReactNode, icon?: ReactNode, to?: string, onClick?: fn }
//     onDismiss?: fn — si viene, "no volver a mostrar" lo llama EN VEZ de
//                 guardar en localStorage (los pop-ups de operadores guardan
//                 el descarte en el servidor, por usuario)
//   }
// Un anuncio de un solo "paso" también funciona: sin flechas ni puntos. El
// `title` de un paso es opcional.
// ─────────────────────────────────────────────────────────────────────────────

export default function AnnouncementModal({ announcement, open, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!announcement) return null;

  const { id, title, steps = [], cta, onDismiss } = announcement;
  const isLast = step === steps.length - 1;
  const current = steps[step];
  const multiStep = steps.length > 1;

  const close = () => {
    if (dontShowAgain) {
      if (onDismiss) onDismiss();
      else dismissAnnouncement(id);
    }
    onClose?.();
  };

  const handleCta = () => {
    close();
    if (cta?.onClick) cta.onClick();
    else if (cta?.to) navigate(cta.to);
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      title={title}
      footer={
        <div className="flex w-full flex-wrap items-center gap-3">
          <label className="mr-auto flex cursor-pointer select-none items-center gap-2 text-sm text-fg-muted">
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand-500"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
            {t("No volver a mostrar este aviso")}
          </label>
          {multiStep && step > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              iconLeft={<ArrowLeft className="h-4 w-4" aria-hidden="true" />}
            >
              {t("Anterior")}
            </Button>
          )}
          {isLast ? (
            cta ? (
              <Button
                variant="primary"
                size="sm"
                onClick={handleCta}
                iconLeft={cta.icon ?? null}
              >
                {cta.label}
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={close}>
                {t("Entendido")}
              </Button>
            )
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              iconRight={<ArrowRight className="h-4 w-4" aria-hidden="true" />}
            >
              {t("Siguiente")}
            </Button>
          )}
        </div>
      }
    >
      <div
        role="group"
        aria-roledescription={multiStep ? "carousel" : undefined}
        aria-label={typeof title === "string" ? title : undefined}
      >
        <div
          key={step}
          className="flex min-h-[190px] flex-col items-center gap-3 px-2 text-center"
          aria-live="polite"
        >
          {current?.icon && (
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-bg-muted text-fg">
              {current.icon}
            </span>
          )}
          {current?.title && (
            <h3 className="text-base font-semibold text-fg">{current.title}</h3>
          )}
          <div className="text-sm text-fg-muted">{current?.body}</div>
        </div>

        {multiStep && (
          <div className="mt-4 flex items-center justify-center gap-2">
            {steps.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setStep(i)}
                aria-label={t("Ir al paso {{n}}", { n: i + 1 })}
                aria-current={i === step ? "step" : undefined}
                className={cn(
                  "h-2.5 rounded-full transition-all",
                  i === step
                    ? "w-6 bg-brand-500"
                    : "w-2.5 bg-border hover:bg-fg-subtle",
                )}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
